import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp 
} from 'firebase/firestore';
import '../../css/accounts/Expenses.css';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form States
  const [expName, setExpName] = useState('');
  const [expDescription, setExpDescription] = useState('');
  const [closingBalance, setClosingBalance] = useState('');
  const [status, setStatus] = useState('Active');
  const [editingId, setEditingId] = useState(null);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Pagination States (15 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 🎯 Fetch Expenses from Firestore 'expenses' collection
  useEffect(() => {
    const q = query(collection(db, 'expenses'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        closingBalance: Number(d.data().closingBalance) || 0,
      }));

      setExpenses(docs);
      setFilteredExpenses(docs);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching expenses: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter Logic
  useEffect(() => {
    let result = expenses;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(exp => 
        exp.expName?.toLowerCase().includes(term) ||
        exp.expCode?.toLowerCase().includes(term) ||
        exp.expDescription?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'All') {
      result = result.filter(exp => exp.status === statusFilter);
    }

    setFilteredExpenses(result);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, expenses]);

  // Handle Form Reset
  const resetForm = () => {
    setExpName('');
    setExpDescription('');
    setClosingBalance('');
    setStatus('Active');
    setEditingId(null);
  };

  // 🎯 Create or Update Expense Ledger
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!expName.trim()) {
      return alert("Expense Name is required!");
    }

    const numericBalance = Number(closingBalance) || 0;

    try {
      if (editingId) {
        // Edit Mode: Update Expense Name, Status, Description, and Closing Balance
        await updateDoc(doc(db, 'expenses', editingId), {
          expName: expName.trim(),
          expDescription: expDescription.trim(),
          closingBalance: numericBalance,
          status: status,
          updatedAt: serverTimestamp(),
        });

        alert("Expense ledger updated successfully!");
      } else {
        // Add Mode: Generate Auto Expense Code (e.g., EXP-1001)
        const expCode = `EXP-${1001 + expenses.length}`;

        await addDoc(collection(db, 'expenses'), {
          expCode: expCode,
          expName: expName.trim(),
          expDescription: expDescription.trim(),
          closingBalance: numericBalance,
          status: status,
          createdAt: serverTimestamp(),
        });

        alert("New expense ledger created successfully!");
      }

      resetForm();
    } catch (error) {
      alert("Error saving expense ledger: " + error.message);
    }
  };

  // Populate Form for Editing
  const handleEditClick = (item) => {
    setEditingId(item.id);
    setExpName(item.expName || '');
    setExpDescription(item.expDescription || '');
    setClosingBalance(item.closingBalance !== undefined ? item.closingBalance : '');
    setStatus(item.status || 'Active');
  };

  // 🎯 Delete Expense Ledger (Check if closingBalance > 0)
  const handleDeleteClick = async (item) => {
    if (item.closingBalance > 0) {
      return alert(`Cannot delete "${item.expName}"! Closing Balance is AED ${item.closingBalance.toFixed(2)} (must be 0 to delete).`);
    }

    if (window.confirm(`Are you sure you want to delete expense ledger "${item.expName}" (${item.expCode})?`)) {
      try {
        await deleteDoc(doc(db, 'expenses', item.id));
        alert("Expense ledger deleted successfully!");
      } catch (error) {
        alert("Error deleting expense: " + error.message);
      }
    }
  };

  // KPI Calculations
  const totalLedgers = filteredExpenses.length;
  const activeLedgers = filteredExpenses.filter(e => e.status === 'Active').length;
  const totalBalance = filteredExpenses.reduce((sum, e) => sum + e.closingBalance, 0);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayedExpenses = filteredExpenses.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="exp-container">
      <div className="exp-card">
        
        {/* Header */}
        <div className="exp-header">
          <div>
            <h2 className="exp-title">Expense Ledgers Management</h2>
            <p className="exp-subtitle">Maintain category ledgers, balances, and expense records</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="exp-kpi-grid">
          <div className="exp-kpi-card kpi-total">
            <span className="exp-kpi-label">Total Ledgers</span>
            <span className="exp-kpi-value">{totalLedgers}</span>
          </div>
          <div className="exp-kpi-card kpi-active">
            <span className="exp-kpi-label">Active Ledgers</span>
            <span className="exp-kpi-value">{activeLedgers}</span>
          </div>
          <div className="exp-kpi-card kpi-balance">
            <span className="exp-kpi-label">Total Closing Balance</span>
            <span className="exp-kpi-value">AED {totalBalance.toFixed(2)}</span>
          </div>
        </div>

        {/* Add/Edit Expense Form Section */}
        <div className="exp-form-wrapper">
          <h3>{editingId ? "✏️ Edit Expense Ledger" : "➕ Create New Expense Ledger"}</h3>
          <form onSubmit={handleSubmit} className="exp-form">
            <div className="form-grid">
              
              <div className="form-group">
                <label>Expense Name <span className="req">*</span></label>
                <input 
                  type="text" 
                  placeholder="e.g., Detergents & Detergent Supplies"
                  value={expName}
                  onChange={(e) => setExpName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Closing Balance (AED)</label>
                <input 
                  type="number" 
                  step="0.01"
                  placeholder="0.00"
                  value={closingBalance}
                  onChange={(e) => setClosingBalance(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <input 
                  type="text" 
                  placeholder="Enter details or vendor description..."
                  value={expDescription}
                  onChange={(e) => setExpDescription(e.target.value)}
                />
              </div>

            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save">
                {editingId ? "Update Ledger" : "Save Expense Ledger"}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="btn-cancel">
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Filters Toolbar */}
        <div className="exp-filter-bar">
          <input 
            type="text" 
            placeholder="Search by Code, Name, or Description..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="exp-input"
          />

          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="exp-select"
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active Only</option>
            <option value="Inactive">Inactive Only</option>
          </select>
        </div>

        {/* Expenses Table */}
        <div className="exp-table-wrapper">
          <table className="exp-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>S.No</th>
                <th>Exp Code</th>
                <th>Expense Name</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Closing Balance</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center', width: '130px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="status-cell">Loading expense ledgers...</td>
                </tr>
              ) : currentDisplayedExpenses.length === 0 ? (
                <tr>
                  <td colSpan="7" className="status-cell">No expense ledgers found.</td>
                </tr>
              ) : (
                currentDisplayedExpenses.map((item, index) => {
                  const serialNumber = indexOfFirstItem + index + 1;
                  const canDelete = item.closingBalance === 0;

                  return (
                    <tr key={item.id} className={editingId === item.id ? 'editing-row' : ''}>
                      <td><strong>{serialNumber}</strong></td>
                      <td className="exp-code-text">{item.expCode || 'N/A'}</td>
                      <td className="exp-name-text">{item.expName}</td>
                      <td className="exp-desc-text">{item.expDescription || '-'}</td>
                      <td className="currency-cell" style={{ textAlign: 'right' }}>
                        AED {item.closingBalance.toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`status-pill ${item.status?.toLowerCase() || 'active'}`}>
                          {item.status || 'Active'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="action-buttons">
                          <button 
                            onClick={() => handleEditClick(item)} 
                            className="btn-edit-sm"
                            title="Edit Ledger"
                          >
                            ✏️ Edit
                          </button>
                          
                          <button 
                            onClick={() => handleDeleteClick(item)} 
                            className={`btn-delete-sm ${!canDelete ? 'disabled-del' : ''}`}
                            title={canDelete ? "Delete Ledger" : "Cannot delete if balance > 0"}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {!loading && filteredExpenses.length > 0 && (
          <div className="exp-pagination-bar">
            <div className="pagination-info">
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredExpenses.length)}</strong> of <strong>{filteredExpenses.length}</strong> records
            </div>

            <div className="pagination-buttons">
              <button 
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="page-btn nav-btn"
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prev && page - prev > 1 && <span className="pagination-dots">...</span>}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`page-btn ${currentPage === page ? 'active-page' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button 
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="page-btn nav-btn"
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Expenses;